import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  useMediaQuery,
  useTheme,
  Button,
  Backdrop,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  Divider,
  Card,
  CardContent,
  Chip,
  Stack,
  InputAdornment,
  Container,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import {
  CashCountRequest,
  CashDrawer,
} from 'api/types';
import {
  deactivateCashDrawer,
  finishCashSaleTransaction,
  getDailyReports,
  getOptimalFloat,
  performCashCount,
  recordCashCount,
  setCashCount,
  startNonSaleTransaction,
  submitCountVariance,
  submitWitness,
  updateCashDrawer,
  upsertCashDrawerStoreStatus,
} from 'api';
import Footer from 'components/Footer';
import { useCurrencyInput } from 'hooks/useCurrencyInput';
import { DenominationInputs } from 'components/DenominationInputs';
import {
  BILL_DENOMINATIONS,
  COIN_DENOMINATIONS,
  ROLLED_COIN_DENOMINATIONS,
  TOLERANCE_AMOUNT,
} from 'services/const';
import { formatSignedCurrency } from 'services/format';
import { useSessionCheckTotal } from 'hooks/useSessionCheckTotal';

interface CashCountResult {
  expected: number;
  actual: number;
  variance: number;
}

type Mode =
  | 'cashCountEntry'
  | 'cashDiscrepancy'
  | 'brinksDepositConfirmation'
  | 'checkCountEntry'
  | 'checkDiscrepancy'
  | 'dailyCloseSummary';

interface LocationState {
  drawer?: CashDrawer;
}
const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));
async function withRetry<T>(fn: () => Promise<T>, step: string, retries = 3, delayMs = 600): Promise<T> {
  let lastErr;
  let d = delayMs;
  for (let i = 0; i <= retries; i++) {
    try { return await fn(); }
    catch (e) {
      lastErr = e;
      if (i === retries) break;
      const jitter = 0.2 + Math.random() * 0.6; // 0.2x..0.8x
      await sleep(Math.floor(d * jitter));
      d = Math.min(d * 2, 6000);
    }
  }
  throw new Error(`${step} - ${String(lastErr)}`);
}
const TOLERANCE = Number(TOLERANCE_AMOUNT || 20.0);

const EndOfDay: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const drawer = (location.state as LocationState)?.drawer;

  const isTabletUp = useMediaQuery(theme.breakpoints.up('sm'));

  const deposit = useCurrencyInput(0);
  const check = useCurrencyInput(0);

  const [frontCounts, setFrontCounts] = useState<Record<string, string>>({});
  const [frontBillTotal, setFrontBillTotal] = useState<number>(0);
  const [frontCoinTotal, setFrontCoinTotal] = useState<number>(0);

  const [backBillCounts, setBackBillCounts] = useState<Record<string, string>>({});
  const [backRolledCounts, setBackRolledCounts] = useState<Record<string, string>>({});
  const [backBillTotal, setBackBillTotal] = useState<number>(0);
  const [backRolledTotal, setBackRolledTotal] = useState<number>(0);
  const [varianceResult, setVarianceResult] = useState<CashCountResult | null>(null);
  const [cashVarianceReason, setCashVarianceReason] = useState<string>('');
  const [mode, setMode] = useState<Mode>('cashCountEntry');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [expectedAmount, setExpectedAmount] = useState<number>(0);
  const [expectedAmountFailed, setExpectedAmountFailed] = useState<boolean>(false);
  const [records, setRecords] = useState<{ receiptNo: string; amount: number }[]>([]);
  const [checkVariance, setCheckVariance] = useState<CashCountResult | null>(null);
  const [checkVarianceReason, setCheckVarianceReason] = useState<string>('');
  const [witness, setWitness] = useState<string>('');
  const [cashTotal, setCashTotal] = useState<number>(0);
  const [checkTotal, setCheckTotal] = useState<number>(0);
  const [consent1, setConsent1] = useState<boolean>(false);
  const [consent2, setConsent2] = useState<boolean>(false);

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMsg, setSnackbarMsg] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [modeHistory, setModeHistory] = useState<Mode[]>([]);
  const [cashTillTab, setCashTillTab] = useState<'front' | 'back'>('front');
  const [tillReminderOpen, setTillReminderOpen] = useState<boolean>(false);
  const [missingTillState, setMissingTillState] = useState<{ front: boolean; back: boolean }>({
    front: false,
    back: false,
  });
  const {
    checkList: checks,
  } = useSessionCheckTotal(
    undefined,
    { enabled: mode === 'checkCountEntry' || mode === 'brinksDepositConfirmation' }
  );

  const toCurrencyValue = (amount: number) => Math.round((amount + Number.EPSILON) * 100) / 100;

  const frontPhysicalTotal = frontBillTotal + frontCoinTotal;
  const backPhysicalTotal = backBillTotal + backRolledTotal;
  const physicalTotal = frontPhysicalTotal + backPhysicalTotal;
  const cashDifference = toCurrencyValue(physicalTotal - expectedAmount);
  const expectedAmountDisplay = expectedAmountFailed ? '—' : `$${expectedAmount.toFixed(2)}`;
  const missingTillLabels = [
    missingTillState.front ? 'front till' : null,
    missingTillState.back ? 'back till' : null,
  ].filter(Boolean) as string[];
  const missingTillDescription =
    missingTillLabels.length === 2
      ? 'the front and back tills'
      : missingTillLabels.length === 1
        ? `the ${missingTillLabels[0]}`
        : 'the selected till';

  const changeMode = (newMode: Mode) => {
    setModeHistory((prev) => [...prev, mode]);
    setMode(newMode);
  };

  const tabProps = (tab: 'front' | 'back') => ({
    id: `${tab}-till-tab`,
    'aria-controls': `${tab}-till-panel`,
  });

  const hasEnteredCounts = (entries: Record<string, string>) =>
    Object.values(entries).some((value) => typeof value === 'string' && value.trim() !== '');

  const handleBack = () => {
    if (modeHistory.length > 0) {
      const prev = modeHistory[modeHistory.length - 1];
      setModeHistory((p) => p.slice(0, -1));
      setMode(prev);
    } else {
      navigate('/');
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const computedCheckTotal = useMemo(
    () => records.reduce((sum, r) => sum + r.amount, 0),
    [records]
  );

  const numericCheckAmount = check.numericValue;

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const { count_id } = await performCashCount({ reason: 'intra_day_balance' });
        const zeroAmountReq: CashCountRequest = {
          counted_amount: { value: 0, currency: 'USD' },
        };
        const cashRecordResponse = await recordCashCount(count_id, zeroAmountReq);
        setExpectedAmount(cashRecordResponse?.expected_amount?.value ?? 0);
        await setCashCount(count_id, '_cancel');
      } catch (error) {
        console.warn('Failed to fetch expected amount, defaulting to 0', error);
        setExpectedAmount(0);
        setExpectedAmountFailed(true);
        setSnackbarSeverity('error');
        setSnackbarMsg('Failed to fetch expected amount');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (mode !== 'checkCountEntry') return;
    const formatted = (checks ?? []).map(({ externalOrderId, amount }) => ({
      receiptNo: externalOrderId ?? '',
      amount,
    }));
    setRecords(formatted);
  }, [mode, checks]);

  // Count Submit → Variance or directly to deposit
  const handleSubmitCount = async () => {
    const frontEntered = hasEnteredCounts(frontCounts);
    const backEntered = hasEnteredCounts(backBillCounts) || hasEnteredCounts(backRolledCounts);

    if (!frontEntered || !backEntered) {
      setMissingTillState({ front: !frontEntered, back: !backEntered });
      setTillReminderOpen(true);
      return;
    }

    await submitCashCounts();
  };

  const submitCashCounts = async () => {
    setIsLoading(true);
    setCashTotal(physicalTotal);

    if (cashDifference !== 0) {
      setVarianceResult({
        expected: expectedAmount,
        actual: physicalTotal,
        variance: cashDifference,
      });
      changeMode('cashDiscrepancy');
      setIsLoading(false);
      return;
    }

    await fetchDepositAmount();
  };

  const handleTillReminderClose = () => {
    setTillReminderOpen(false);
    setMissingTillState({ front: false, back: false });
  };

  const handleTillReminderProceed = async () => {
    handleTillReminderClose();
    await submitCashCounts();
  };

  const handleTillReminderNavigate = (tab: 'front' | 'back') => {
    handleTillReminderClose();
    setCashTillTab(tab);
  };

  // Fetch float/deposit amount
  const fetchDepositAmount = (): Promise<void> => {
    setIsLoading(true);
    return getOptimalFloat()
      .then((optimalFloat: number) => {
        const rawDeposit = physicalTotal - optimalFloat;
        // round to cents to neutralize values like 49.999999999
        const cents = Math.max(0, Math.round(rawDeposit * 100));
        //truncate to dollars (no pennies, no rounding up)
        const depositValue = Math.floor(cents / 100);
        deposit.setNumber(depositValue);
      })
      .catch((error) => {
        setSnackbarSeverity('error');
        setSnackbarMsg('Fetch deposit amount failed, defaulting to 0');
        console.warn('Fetch deposit amount failed, defaulting to 0', error);
        deposit.setNumber(0);
      })
      .finally(() => {
        changeMode('brinksDepositConfirmation');
        setIsLoading(false);
      });
  };

  // Cash discrepancy handlers
  const handleRecountCash = () => {
    setVarianceResult(null);
    setCashVarianceReason('');
    setModeHistory([]);
    changeMode('cashCountEntry');
  };
  const handleSubmitCashVariance = async () => {
    await fetchDepositAmount();
  };

  // Check discrepancy handlers
  const handleRecountCheck = () => {
    setCheckVariance(null);
    setCheckVarianceReason('');
    setModeHistory(['cashCountEntry', 'cashDiscrepancy', 'brinksDepositConfirmation']);
    changeMode('checkCountEntry');
  };
  const handleSubmitCheckVariance = () => {
    if (Math.abs(checkVariance?.variance ?? 0) <= TOLERANCE || checkVarianceReason.trim()) {
      changeMode('dailyCloseSummary');
    }
  };

  const handleConfirmDeposit = async () => {
    setIsLoading(true);
    try {
      if (checks.length) {
        const formatted = checks.map(({ externalOrderId, amount }) => ({
          receiptNo: externalOrderId ?? '',
          amount,
        }));
        setRecords(formatted);
      }
      changeMode('checkCountEntry');

    } catch (error) {
      console.warn('Failed To Fetch Check Records!', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitCheckCount = () => {
    setIsLoading(true);
    const expected = computedCheckTotal;
    const actual = numericCheckAmount;
    const variance = actual - expected;
    setCheckTotal(actual);
    setCheckVariance({ expected, actual, variance });
    if (variance === 0) {
      changeMode('dailyCloseSummary');
    } else {
      changeMode('checkDiscrepancy');
    }
    setIsLoading(false);
  };

  const handleCloseDay = async () => {
    setIsLoading(true);
    try {
      const depositValue = deposit.numericValue;
      const storeId = drawer?.cash_drawer_id ?? '';
      const remainingFloat = toCurrencyValue(cashTotal - depositValue);

      // 1. Start Non-Sale Transaction for Brinks deposit
      const startResponse = await withRetry(
        () => startNonSaleTransaction({
          transaction_type: 'transfer_out',
          reason_code: 'brinks',
          amount_out: { value: depositValue, currency: 'USD' },
        }),
        'startNonSaleTransaction',
        3, 800
      );
      const transactionId = startResponse.transaction_id;

      // 2. Finish the transaction
      await withRetry(
        () => finishCashSaleTransaction(transactionId, { status: 'completed' }),
        'finishCashSaleTransaction',
        3, 800
      );

      // 3. Perform end-of-day cash count
      const performResponse = await withRetry(
        () => performCashCount({ reason: 'end_of_day_balance' }), 'performCashCount', 2, 500
      );
      const countId: string = performResponse.count_id;

      // 4. Record cash count
      const recordResponse = await withRetry(
        () => recordCashCount(countId, { counted_amount: { value: remainingFloat, currency: 'USD' } }),
        'recordCashCount',
        2, 500
      );
      const discrepancyExists = !!recordResponse.discrepancy;

      // 5. Submit the cash count
      await withRetry(() => setCashCount(countId, '_submit'), 'setCashCount', 3, 800);

      // 6. Update the cash drawer float
      await withRetry(
        () => updateCashDrawer({ name: storeId || '', float: remainingFloat }),
        'updateCashDrawer',
        3, 800
      );

      // 7. Submit check & cash variance if provided
      const cashVarianceAmount =
        discrepancyExists && varianceResult ? varianceResult.variance : null;

      const payloadForVariance = {
        storeId,
        countId,
        cashVarianceAmount,
        cashVarianceReason: cashVarianceReason?.trim() || null,
        checkVarianceAmount: checkVariance?.variance ?? null,
        checkVarianceReason: checkVarianceReason?.trim() || null,
      };

      const shouldSubmitVariance =
        payloadForVariance.cashVarianceAmount != null ||
        payloadForVariance.checkVarianceAmount != null ||
        payloadForVariance.cashVarianceReason != null ||
        payloadForVariance.checkVarianceReason != null;

      if (shouldSubmitVariance) {
        await withRetry(() => submitCountVariance(payloadForVariance),
          'submitCountVariance', 2, 1000);
      }

      // 8. Submit witness
      if (witness?.trim()) {
        await withRetry(
          () => submitWitness({ storeId, countId, witnessName: witness.trim() }),
          'submitWitness',
          2, 1000
        );
      }

      // 9. Deactivate drawer
      await withRetry(() => deactivateCashDrawer({ count_id: countId }), 'deactivateCashDrawer', 3, 800);

      const {
        activationTimestamp,
        deactivationTimestamp,
      } = await fetchLatestSessionTimestamps();

      // 10. Upsert store status as CLOSED with deactivation timestamp (UTC)
      await withRetry(
        () =>
          upsertCashDrawerStoreStatus({
            activationTimestamp,
            deactivationTimestamp
          }),
        'upsertCashDrawerStoreStatus',
        3,
        800
      );
      // 11. Toast + navigate
      setSnackbarSeverity('success');
      setSnackbarMsg('Day closed successfully');
      setSnackbarOpen(true);
      setTimeout(() => navigate('/'), 1000);

    } catch (error) {
      console.error('handleCloseDay failed:', error);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const data = (error as { response?: { data?: unknown; status?: number; statusText?: string } }).response?.data;

      const errorMsg = typeof data === 'string' ? data
        : (data && typeof (data as { message?: unknown }).message === 'string')
          ? (data as { message: string }).message
          : error instanceof Error
            ? error.message
            : String(error);
      setSnackbarSeverity('error');
      setSnackbarMsg(errorMsg);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  async function fetchLatestSessionTimestamps() {
    try {
      const { reports } = await getDailyReports();
      const latest = reports[0];

      return {
        activationTimestamp: latest.activation_timestamp ?? null,
        deactivationTimestamp: latest.deactivation_timestamp ?? null,
      };
    } catch (err) {
      console.error(err);
      return { activationTimestamp: null, deactivationTimestamp: null };
    }
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">End of Day</Typography>}
        subtitle="Close out the cash drawer"
        showBackButton
        onBack={handleBack}
        sticky
        align="left"
        elevateOnScroll
      />

      <Backdrop open={isLoading} sx={(t) => ({ color: '#fff', zIndex: t.zIndex.modal + 1 })}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container
        disableGutters
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 720, md: 880 },
          mx: 'auto',
          pb: mode === 'cashCountEntry' ? 0 : { xs: 18, sm: 20 },
        }}
      >
        <Paper
          elevation={0}
          component="main"
          sx={{
            mx: 2,
            mt: 2,
            mb: 6,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
            background: alpha(theme.palette.primary.light, 0.05),
            backdropFilter: 'saturate(120%) blur(4px)',
          }}
        >
          {/* Context / Drawer info */}
          <Card
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            <CardContent
              sx={{
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                '& .MuiChip-root': { borderRadius: 2 },
              }}
            >
              <Chip
                size="small"
                variant="outlined"
                label={`Drawer: ${drawer?.cash_drawer_id ?? '—'}`}
              />

              <Chip
                size="small"
                color={drawer?.status === 'active' ? 'success' : 'default'}
                variant={drawer?.status === 'active' ? 'filled' : 'outlined'}
                label={`Status: ${drawer?.status ?? '—'}`}
              />
            </CardContent>
          </Card>

          {/* Panels */}
          {/* CASH COUNT ENTRY */}
          {mode === 'cashCountEntry' && (
            <Paper elevation={0} sx={{ mt: 2 }}>
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                  boxShadow: '0 16px 40px rgba(16, 24, 40, 0.12)',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)}, ${alpha(
                    theme.palette.primary.light,
                    0.1
                  )})`,
                }}
              >
                <Tabs
                  value={cashTillTab}
                  onChange={(_, value) => setCashTillTab(value as 'front' | 'back')}
                  variant="fullWidth"
                  sx={{
                    px: { xs: 1, sm: 2.5 },
                    pt: { xs: 1, sm: 1.5 },
                    background: alpha(theme.palette.background.paper, 0.9),
                    backdropFilter: 'blur(8px)',
                    '& .MuiTabs-flexContainer': {
                      gap: { xs: 0.75, sm: 1.5 },
                    },
                    '& .MuiTab-root': {
                      minHeight: { xs: 64, sm: 72 },
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: { xs: 14, sm: 16 },
                      alignItems: 'flex-start',
                      color: alpha(theme.palette.text.primary, 0.75),
                    },
                    '& .Mui-selected': {
                      color: `${theme.palette.primary.main} !important`,
                    },
                    '& .MuiTabs-indicator': {
                      height: 4,
                      borderRadius: '4px 4px 0 0',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    },
                  }}
                >
                  <Tab
                    value="front"
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                        <Typography component="span" fontWeight={700}>
                          Front Till
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ opacity: 0.75 }}>
                          Active drawer count
                        </Typography>
                      </Box>
                    }
                    {...tabProps('front')}
                  />
                  <Divider orientation="vertical" flexItem sx={{ borderColor: alpha(theme.palette.divider, 0.08) }} />
                  <Tab
                    value="back"
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                        <Typography component="span" fontWeight={700}>
                          Back Till
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ opacity: 0.75 }}>
                          Reserve till & rolled coins
                        </Typography>
                      </Box>
                    }
                    {...tabProps('back')}
                  />
                </Tabs>
                <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.08) }} />
                <Box
                  role="tabpanel"
                  hidden={cashTillTab !== 'front'}
                  id="front-till-panel"
                  aria-labelledby="front-till-tab"
                  sx={{
                    display: cashTillTab === 'front' ? 'block' : 'none',
                    p: { xs: 2.5, sm: 3.5 },
                    background: alpha(theme.palette.background.paper, 0.95),
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    Front Till
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Count the active drawer to verify the till balance.
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 3, mb: 1.5 }}>
                    <Typography fontWeight={600}>Expected Count (total)</Typography>
                    <Typography fontWeight={700}>{expectedAmountDisplay}</Typography>
                  </Stack>

                  <Divider sx={{ my: 2.5 }} />

                  <DenominationInputs
                    bills={BILL_DENOMINATIONS}
                    coins={COIN_DENOMINATIONS}
                    values={frontCounts}
                    onValuesChange={setFrontCounts}
                    onBillTotalChange={setFrontBillTotal}
                    onCoinTotalChange={setFrontCoinTotal}
                  />

                  <Card
                    variant="outlined"
                    sx={{
                      mt: 3.5,
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.divider, 0.9),
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Chip label={`Bills: $${frontBillTotal.toFixed(2)}`} variant="outlined" sx={{ borderRadius: 2 }} />
                        <Chip label={`Coins: $${frontCoinTotal.toFixed(2)}`} variant="outlined" sx={{ borderRadius: 2 }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Chip
                          color="primary"
                          label={`Front Cash: $${frontPhysicalTotal.toFixed(2)}`}
                          sx={{
                            borderRadius: 2,
                            fontSize: { xs: 16, sm: 18 },
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                <Box
                  role="tabpanel"
                  hidden={cashTillTab !== 'back'}
                  id="back-till-panel"
                  aria-labelledby="back-till-tab"
                  sx={{
                    display: cashTillTab === 'back' ? 'block' : 'none',
                    p: { xs: 2.5, sm: 3.5 },
                    background: alpha(theme.palette.background.paper, 0.95),
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    Back Till
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Count the reserve till and any rolled coins held in the safe.
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 3, mb: 1.5 }}>
                    <Typography fontWeight={600}>Expected Count (total)</Typography>
                    <Typography fontWeight={700}>{expectedAmountDisplay}</Typography>
                  </Stack>
                  <Divider sx={{ my: 2.5 }} />

                  <Box sx={{ mt: 3 }}>
                    <DenominationInputs
                      bills={BILL_DENOMINATIONS}
                      coins={[]}
                      values={backBillCounts}
                      onValuesChange={setBackBillCounts}
                      onBillTotalChange={setBackBillTotal}
                      billsLabel="Bills"
                    />
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <DenominationInputs
                      bills={[]}
                      coins={ROLLED_COIN_DENOMINATIONS}
                      values={backRolledCounts}
                      onValuesChange={setBackRolledCounts}
                      onCoinTotalChange={setBackRolledTotal}
                      coinsLabel="Rolled Coins"
                    />
                  </Box>

                  <Card
                    variant="outlined"
                    sx={{
                      mt: 3.5,
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.divider, 0.9),
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Chip label={`Bills: $${backBillTotal.toFixed(2)}`} variant="outlined" sx={{ borderRadius: 2 }} />
                        <Chip
                          label={`Rolled Coins: $${backRolledTotal.toFixed(2)}`}
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Chip
                          color="primary"
                          label={`Back Cash: $${backPhysicalTotal.toFixed(2)}`}
                          sx={{
                            borderRadius: 2,
                            fontSize: { xs: 16, sm: 18 },
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              <Card
                variant="outlined"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.divider, 0.9),
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Chip label={`Front Cash: $${frontPhysicalTotal.toFixed(2)}`} variant="outlined" sx={{ borderRadius: 2 }} />
                    <Chip label={`Back Cash: $${backPhysicalTotal.toFixed(2)}`} variant="outlined" sx={{ borderRadius: 2 }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Chip
                      color="primary"
                      label={`Total Counted Cash: $${physicalTotal.toFixed(2)}`}
                      sx={{
                        borderRadius: 2,
                        fontSize: { xs: 16, sm: 18 },
                        fontWeight: 600,
                        px: 1,
                        py: 1,
                        whiteSpace: 'normal',
                        textAlign: 'center',
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Paper>
          )}

          {/* CASH DISCREPANCY */}
          {mode === 'cashDiscrepancy' && varianceResult && (
            <Paper elevation={0} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Cash Discrepancy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {`A discrepancy has been found in the count. ${Math.abs(varianceResult?.variance ?? 0) > TOLERANCE
                  ? 'Please provide a reason for the discrepancy.'
                  : `This variance is within the allowed ±$${TOLERANCE.toFixed(2)} tolerance.`
                  }`}
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap={1.25} columnGap={1.25} mb={2}>
                <Typography fontWeight={600}>Expected</Typography>
                <Typography textAlign="right" color="success.main">
                  ${varianceResult.expected.toFixed(2)}
                </Typography>
                <Typography fontWeight={600}>Count</Typography>
                <Typography textAlign="right">${varianceResult.actual.toFixed(2)}</Typography>
                <Typography fontWeight={600}>Variance</Typography>
                <Typography
                  textAlign="right"
                  color={varianceResult.variance >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatSignedCurrency(varianceResult.variance)}
                </Typography>
              </Box>

              {Math.abs(varianceResult?.variance ?? 0) > TOLERANCE && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Provide a reason for the discrepancy"
                  variant="outlined"
                  value={cashVarianceReason}
                  onChange={(e) => setCashVarianceReason(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                />
              )}
            </Paper>
          )}

          {/* BRINKS DEPOSIT */}
          {mode === 'brinksDepositConfirmation' && (
            <Paper elevation={0} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Brinks Deposit
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please deposit the following amount to Brinks.
              </Typography>

              <TextField
                fullWidth
                type="text"
                placeholder="Enter amount"
                inputRef={deposit.inputRef}
                value={deposit.formattedValue}
                onFocus={deposit.onFocus}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    transition: 'box-shadow .2s ease',
                    '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                  },
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    fontSize: isTabletUp ? 24 : 20,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    caretColor: 'transparent',
                  },
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '^[0-9]*$',
                  enterKeyHint: 'done',
                }}
              />
            </Paper>
          )}

          {/* CHECK COUNT ENTRY */}
          {mode === 'checkCountEntry' && (
            <Paper elevation={0} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Check Count
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please confirm the check amount below.
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap={1} columnGap={1} sx={{ mb: 2 }}>
                <Typography fontWeight={600}>Expected</Typography>
                <Typography textAlign="right" color="success.main">
                  ${computedCheckTotal.toFixed(2)}
                </Typography>
              </Box>

              {records.length > 0 ? (
                <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.85)}` }}>
                  <Table size="small" aria-label="check count table">
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.palette.grey[900] }}>
                        <TableCell sx={{ color: '#fff' }}>Receipt No</TableCell>
                        <TableCell align="right" sx={{ color: '#fff' }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map((r) => (
                        <TableRow key={r.receiptNo}>
                          <TableCell>{r.receiptNo}</TableCell>
                          <TableCell align="right">${r.amount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography>No checks to display.</Typography>
                </Box>
              )}

              <TextField
                fullWidth
                type="text"
                placeholder="Enter amount"
                inputRef={check.inputRef}
                value={check.formattedValue}
                onKeyDown={check.onKeyDown}
                onFocus={check.onFocus}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    transition: 'box-shadow .2s ease',
                    '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                  },
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    fontSize: isTabletUp ? 24 : 20,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    caretColor: 'transparent',
                  },
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  enterKeyHint: 'done',
                }}
              />
            </Paper>
          )}

          {/* CHECK DISCREPANCY */}
          {mode === 'checkDiscrepancy' && checkVariance && (
            <Paper elevation={0} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Check Discrepancy
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {`A discrepancy has been found in the count. ${Math.abs(checkVariance?.variance ?? 0) > TOLERANCE
                  ? 'Please provide a reason for the discrepancy.'
                  : `This variance is within the allowed ±$${TOLERANCE.toFixed(2)} tolerance.`
                  }`}
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap={1.25} columnGap={1.25} mb={2}>
                <Typography fontWeight={600}>Expected</Typography>
                <Typography textAlign="right" color="success.main">
                  ${checkVariance.expected.toFixed(2)}
                </Typography>
                <Typography fontWeight={600}>Count</Typography>
                <Typography textAlign="right">${checkVariance.actual.toFixed(2)}</Typography>
                <Typography fontWeight={600}>Variance</Typography>
                <Typography
                  textAlign="right"
                  color={checkVariance.variance >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatSignedCurrency(checkVariance.variance)}
                </Typography>
              </Box>

              {Math.abs(checkVariance?.variance ?? 0) > TOLERANCE && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Provide a reason for the discrepancy"
                  variant="outlined"
                  value={checkVarianceReason}
                  onChange={(e) => setCheckVarianceReason(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                />
              )}
            </Paper>
          )}

          {/* DAILY CLOSE SUMMARY */}
          {mode === 'dailyCloseSummary' && (
            <Paper elevation={0} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Counts
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography>Cash</Typography>
                  <Typography fontWeight={700}>${cashTotal.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography>Brinks Deposit</Typography>
                  <Typography fontWeight={700}>${deposit.formattedValue}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>Checks</Typography>
                  <Typography fontWeight={700}>${checkTotal.toFixed(2)}</Typography>
                </Stack>
              </Box>

              <TextField
                placeholder="Enter a witness name"
                variant="outlined"
                value={witness}
                onChange={(e) => setWitness(e.target.value)}
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                sx={{ mb: 3 }}
              />

              {deposit.numericValue > 0 && (
                <FormControlLabel
                  control={<Checkbox checked={consent1} onChange={(e) => setConsent1(e.target.checked)} />}
                  label="I confirm that the cash has been deposited into the safe"
                  sx={{ mb: 1 }}
                />
              )}
              {checkTotal > 0 && (
                <FormControlLabel
                  control={<Checkbox checked={consent2} onChange={(e) => setConsent2(e.target.checked)} />}
                  label="I confirm that the checks has been processed"
                />
              )}
            </Paper>
          )}
        </Paper>
      </Container>
      {/* Footer (shared): reserves space automatically, no overlap */}
      {mode === 'cashCountEntry' && (
        <Footer
          onSubmit={handleSubmitCount}
          disabled={isLoading || physicalTotal === 0}
          label="Submit Count"
          elevated
          position="fixed"
          topContent={
            <Stack
              direction="row"
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
            >
              {[
                { label: 'Expected', value: expectedAmountDisplay, color: 'primary.main' },
                { label: 'Counted', value: `$${physicalTotal.toFixed(2)}`, color: 'text.primary' },
                {
                  label: 'Difference',
                  value: expectedAmountFailed ? '—' : formatSignedCurrency(cashDifference),
                  color: expectedAmountFailed
                    ? 'text.secondary'
                    : cashDifference >= 0
                      ? 'success.main'
                      : 'error.main',
                },
              ].map(({ label, value, color }) => (
                <Box key={label} sx={{ flex: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography fontWeight={700} color={color}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          }
        />
      )}

      {mode === 'cashDiscrepancy' && (
        <Footer elevated reserveSpace={false} position="fixed">
          <Button variant="outlined" color="error" fullWidth onClick={handleRecountCash}>
            Recount
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={(Math.abs(varianceResult?.variance ?? 0) > TOLERANCE && !cashVarianceReason.trim()) || isLoading}
            onClick={handleSubmitCashVariance}
          >
            Submit
          </Button>
        </Footer>
      )}

      {mode === 'brinksDepositConfirmation' && (
        <Footer
          onSubmit={handleConfirmDeposit}
          label="Confirm Deposit"
          elevated
          reserveSpace={false}
          position="fixed"
        />
      )}

      {mode === 'checkCountEntry' && (
        <Footer
          onSubmit={handleSubmitCheckCount}
          disabled={isLoading || numericCheckAmount < 0}
          label="Submit Count"
          elevated
          reserveSpace={false}
          position="fixed"
        />
      )}

      {mode === 'checkDiscrepancy' && (
        <Footer elevated reserveSpace={false} position="fixed">
          <Button variant="outlined" color="error" fullWidth onClick={handleRecountCheck}>
            Recount
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={isLoading || (Math.abs(checkVariance?.variance ?? 0) > TOLERANCE && !checkVarianceReason.trim())}
            onClick={handleSubmitCheckVariance}
          >
            Submit
          </Button>
        </Footer>
      )}

      {mode === 'dailyCloseSummary' && (
        <Footer
          onSubmit={handleCloseDay}
          disabled={!witness.trim() || (deposit.numericValue > 0 && !consent1) || (checkTotal > 0 && !consent2)}
          label="Close Day"
          elevated
          reserveSpace={false}
          position="fixed"
        />
      )}

      <Dialog
        open={tillReminderOpen}
        onClose={handleTillReminderClose}
        aria-labelledby="till-reminder-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: 320, sm: 400 },
            p: { xs: 1, sm: 0 },
          },
        }}
      >
        <DialogTitle id="till-reminder-title" sx={{ fontWeight: 700 }}>
          Finish Both Tills?
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <DialogContentText>
            {`We noticed you haven't entered a count for ${missingTillDescription}. You can jump back to finish it or submit anyway.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexWrap: 'wrap' }}>
          {missingTillState.front && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleTillReminderNavigate('front')}
            >
              Go to Front Till
            </Button>
          )}
          {missingTillState.back && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleTillReminderNavigate('back')}
            >
              Go to Back Till
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleTillReminderProceed()}
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        sx={{ bottom: '100px' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EndOfDay;
